import { ActionButton } from "@/components/atoms/action-button";
import { Show } from "@/components/atoms/show";
import { Card, CardTitle } from "@/components/ui/card";
import {
  FormLabel,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useFormContext } from "react-hook-form";
import { CollectionFormScreenProps } from "../../types";
import { TestimonialFormTypes } from "../../utils/schema";
import { useFileUpload } from "@/hooks/use-file-upload";
import { getCollectionFormStyles } from "../../utils/extract-review-form-styles";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { useEffect } from "react";

export const CustomerFormScreen = ({ data }: CollectionFormScreenProps) => {
  const form = useFormContext<TestimonialFormTypes>();
  const styles = getCollectionFormStyles(data);

  const {
    files: images,
    getRootProps,
    getInputProps,
  } = useFileUpload({
    maxFiles: 1,
    acceptedFileTypes: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif"],
    },
  });

  useEffect(() => {
    if (images.length > 0) {
      form.setValue("avatar", images[0].file);
    }
  }, [form, images]);

  return (
    <Card
      className={cn(
        styles.cardPadding,
        styles.cardSpacing,
        "testify-max-w-xl testify-w-full",
      )}
      style={{
        backgroundColor: styles["secondaryColor"],
        color: styles["secondaryTextColor"],
      }}
    >
      <div className="testify-p-6 testify-border-b">
        <CardTitle className="testify-text-xl sm:testify-text-2xl">
          Personal Information
        </CardTitle>
        <p className="testify-text-sm sm:testify-text-base testify-text-muted-foreground testify-mt-1">
          Tell us a bit about yourself
        </p>
      </div>

      {/* Profile Photo Section */}
      <Show when={data.formFields.photo.enabled}>
        <div className="testify-space-y-4">
          <FormLabel>{data.labels.photo}</FormLabel>
          <div
            {...getRootProps()}
            className="testify-flex testify-items-center testify-gap-4 testify-p-2 testify-border testify-border-dashed testify-rounded-lg hover:testify-border-primary/20 testify-transition-colors testify-cursor-pointer"
          >
            <Avatar className="testify-h-16 testify-w-16">
              <AvatarImage
                src={images[0]?.preview || `/default-avatar.jpeg`}
                className="testify-object-cover"
              />
            </Avatar>
            <input {...getInputProps()} />
            <div className="testify-flex-1">
              <p className="testify-text-sm testify-font-medium">
                {data.placeholder.photo}
              </p>
              <p className="testify-text-sm testify-text-muted-foreground">
                Drop an image here or click to browse
              </p>
            </div>
          </div>
        </div>
      </Show>

      {/* Personal Details Section */}
      <div className="testify-flex testify-flex-col testify-justify-between testify-gap-2 lg:testify-flex-row">
        <Show when={data.formFields.name.enabled}>
          <FormField
            control={form.control}
            name="customer.name"
            render={({ field }) => (
              <FormItem className="testify-w-full">
                <FormLabel>{data.labels.name}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={data.placeholder.name}
                    {...field}
                    required={data.formFields.name.required}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Show>

        <Show when={data.formFields.headline.enabled}>
          <FormField
            control={form.control}
            name="customer.title"
            render={({ field }) => (
              <FormItem className="testify-w-full">
                <FormLabel>{data.labels.headline}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={data.placeholder.headline}
                    {...field}
                    required={data.formFields.headline.required}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Show>
      </div>

      {/* Contact Information Section */}
      <div className="testify-space-y-4">
        <Show when={data.formFields.email.enabled}>
          <FormField
            control={form.control}
            name="customer.email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{data.labels.email}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={data.placeholder.email}
                    {...field}
                    required={data.formFields.email.required}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Show>

        <Show when={data.formFields.link.enabled}>
          <FormField
            control={form.control}
            name="customer.url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{data.labels.link}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={data.placeholder.link}
                    {...field}
                    required={data.formFields.link.required}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Show>
      </div>

      {/* Consent Section */}
      <FormField
        control={form.control}
        name="approved"
        render={({ field }) => (
          <FormItem className="testify-flex testify-items-center testify-space-x-3 !testify-mt-8">
            <FormControl>
              <Checkbox
                className="!testify-mt-0"
                checked={field.value}
                onCheckedChange={field.onChange}
                required={data.formFields.consent.required}
              />
            </FormControl>
            <div className="testify-flex testify-flex-col testify-gap-1">
              <FormLabel className="testify-text-sm sm:testify-text-base testify-leading-relaxed">
                {data.labels.consent}
              </FormLabel>
              <p className="testify-text-xs sm:testify-text-sm testify-text-muted-foreground">
                By checking this box, you agree to share your testimonial
              </p>
            </div>
          </FormItem>
        )}
      />

      {/* Submit Button */}
      <div className="testify-pt-4">
        <ActionButton
          className="testify-w-full"
          type="submit"
          size="lg"
          loading={form.formState.isSubmitting}
          style={{
            background: styles["primaryColor"],
            color: styles["primaryTextColor"],
          }}
          onClick={() => console.log(form.formState.errors)}
        >
          {data.labels.submitButton}
        </ActionButton>
      </div>
    </Card>
  );
};
